/**
 * Feature E08-F02 — "IMAP/SMTP fallback" contracts.
 *
 * 1:1 port of the legacy n8n traditional (non-OAuth) mail path:
 *   - IMAP read  — docs/source-n8n/reference/EmailChecker.md "Fetch Emails" +
 *     Att.md "Att: Fetch from IMAP" (the internal `aiac-imap-proxy` HTTP service
 *     POST /fetch-emails | /search-emails);
 *   - SMTP send  — docs/source-n8n/reference/Email.md "Email: Send via SMTP"
 *     (emailSend with DYNAMIC creds from `mailbox_accounts`).
 *
 * This file is the single source of truth for the IMAP/SMTP fallback layer:
 *   - the credential bundle read off a `mailbox_accounts` row;
 *   - the IMAP fetch parameters (since_date / limit=100 / the explicit
 *     Is Flagged=false + Is Recent=false filters) shared by the direct IMAP
 *     client (T01) and the HTTP proxy adapter (T05);
 *   - the unified fetched-email shape both produce (same as the classifier input);
 *   - the SMTP outbound message shape and the per-account size cap for inlined
 *     attachment bytes (T05: content_base64 only for files < 10 MB).
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 *
 * NB: the existing E04-F03 fetcher (services/emailFetcher.ts) covers the OAuth
 * Graph path and the *legacy 50-row* proxy call inside the Email-Checker worker.
 * E08-F02 is the dedicated traditional IMAP/SMTP *fallback* layer described in
 * backlog.md#imapsmtp-fallback (direct-protocol client + per-account SMTP +
 * the attachment-capable proxy read path), so its own constants live here and
 * do NOT mutate the E04-F03 contracts.
 */
import { z } from 'zod';
// B1b/KS-17 — ONE attachment shape shared by the Graph and SMTP branches, so the
// two transports cannot drift on how a file is represented.
import { outboundAttachmentSchema } from './email-send.js';
// ---------------------------------------------------------------------------
// IMAP fetch parameters (Fetch Emails / Att: Fetch from IMAP)
// ---------------------------------------------------------------------------
/**
 * First-run IMAP `SINCE` default when a mailbox has never been checked
 * (legacy `last_checked_at || '2020-01-01T00:00:00Z'`). Kept independently of
 * the E04-F03 constant so this fallback layer owns its own value (same literal).
 */
export const IMAP_FALLBACK_SINCE_DATE = '2020-01-01T00:00:00Z';
/**
 * Per-fetch hard cap for the fallback IMAP path. The backlog spec
 * (E08-F02-T01) pins this at **100** (not the Email-Checker `limit:50`): the
 * traditional INBOX scan pulls up to 100 messages since `last_checked_at`.
 */
export const IMAP_FALLBACK_FETCH_LIMIT = 100;
/**
 * The two IMAP search flags the legacy node sets EXPLICITLY to `false`
 * (EmailChecker / n8n IMAP node "Custom Email Rules"). Both MUST be applied —
 * leaving them on (`Is Recent` / `Is Flagged` defaulting true) silently drops
 * most messages (T01 acceptance / source gotcha). `Is Seen` is deliberately
 * NOT constrained, so both read and unread mail is fetched.
 */
export const IMAP_FALLBACK_SEARCH_FLAGS = {
    /** Is Flagged = false (do not require the \Flagged flag). */
    flagged: false,
    /** Is Recent = false (do not require the \Recent flag). */
    recent: false,
};
/**
 * Attachment inlining cap (T05): the proxy returns `content_base64` ONLY for
 * files smaller than this; larger attachments come back as metadata only.
 * 10 MB, in bytes.
 */
export const IMAP_ATTACHMENT_INLINE_MAX_BYTES = 10 * 1024 * 1024;
// ---------------------------------------------------------------------------
// Credential bundle off a `mailbox_accounts` row
// ---------------------------------------------------------------------------
/**
 * IMAP read credentials for ONE mailbox (Att.md "Att: Get IMAP Credentials" /
 * EmailChecker "Set IMAP Params"). Host/port/user/password come dynamically
 * from the `mailbox_accounts` row — never a shared/static transport.
 */
export const imapCredentialsSchema = z.object({
    host: z.string(),
    port: z.number().int(),
    user: z.string(),
    password: z.string(),
    /** Legacy hard-coded `tls:true`. */
    tls: z.boolean().default(true),
    /** ISO 8601; effective SINCE (last_checked_at || IMAP_FALLBACK_SINCE_DATE). */
    sinceDate: z.string(),
    /** Legacy `limit` (defaults to IMAP_FALLBACK_FETCH_LIMIT). */
    limit: z.number().int().positive().default(IMAP_FALLBACK_FETCH_LIMIT),
});
/**
 * SMTP send credentials + sender identity for ONE mailbox (Email.md
 * "Email: Send via SMTP", per-account creds). `senderEmail` is the mailbox
 * `email` column and becomes the `from` of the message — never hard-coded.
 */
export const smtpCredentialsSchema = z.object({
    host: z.string(),
    port: z.number().int(),
    user: z.string(),
    password: z.string(),
    /** mailbox_accounts.email → message `from` (sender identity). */
    senderEmail: z.string(),
});
// ---------------------------------------------------------------------------
// Transport selection (Check Auth Type — oauth vs imap)
// ---------------------------------------------------------------------------
/**
 * The two transports the `auth_type` fork routes to (Check Auth Type node):
 *   - `graph` — `auth_type === 'oauth'` → Microsoft Graph read/send;
 *   - `imap`  — everything else        → IMAP read + SMTP send (this layer).
 */
export const MAIL_TRANSPORTS = ['graph', 'imap'];
/**
 * Resolve the transport for a mailbox by its `auth_type` (Check Auth Type:
 * strict string equality `auth_type === 'oauth'`). Any non-`'oauth'` value
 * (the default 'imap', null, '') falls back to the IMAP/SMTP path.
 */
export function transportForAuthType(authType) {
    return authType === 'oauth' ? 'graph' : 'imap';
}
// ---------------------------------------------------------------------------
// Unified attachment shape (proxy/search returns content_base64)
// ---------------------------------------------------------------------------
/**
 * One attachment as returned by the proxy `/search-emails` (include_attachments).
 * `contentBase64` is present ONLY for files < IMAP_ATTACHMENT_INLINE_MAX_BYTES
 * (T05); larger files keep `size` metadata but `contentBase64` is null.
 */
export const imapProxyAttachmentSchema = z.object({
    filename: z.string(),
    contentType: z.string().optional(),
    size: z.number().int().nonnegative().optional(),
    /** base64 payload, or null when omitted (≥10 MB or metadata-only fetch). */
    contentBase64: z.string().nullable().default(null),
});
// ---------------------------------------------------------------------------
// SMTP outbound message (Email: Set Send Params / Send via SMTP)
// ---------------------------------------------------------------------------
/**
 * A single SMTP outbound message. `from` is the mailbox sender_email (not a
 * request field). `html` is the already-wrapped body; `text` is the optional
 * plain-text alternative. `replyTo` carries `in_reply_to` for threading
 * (SMTP-only — the Graph branch does not, Email.md gotcha).
 */
export const smtpOutboundSchema = z.object({
    from: z.string(),
    to: z.string().min(1),
    subject: z.string().default(''),
    html: z.string().default(''),
    text: z.string().optional(),
    cc: z.array(z.string()).optional().default([]),
    /** E05-F06-T11 — blind copies (nodemailer `bcc`); omitted when empty. */
    bcc: z.array(z.string()).optional(),
    replyTo: z.string().optional(),
    /**
     * B1b/KS-17 — MIME attachments (base64), the SMTP mirror of the Graph
     * `fileAttachment` branch. `sendViaSmtp` spreads the message through untouched,
     * so only the nodemailer driver maps this onto `transport.sendMail`.
     */
    attachments: z.array(outboundAttachmentSchema).optional(),
});
//# sourceMappingURL=imap-smtp-fallback.js.map